import { useCallback, useMemo, useContext } from 'react';
import { css } from '@emotion/react';
import { DeviceType_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/callqueue-config/v1/callqueue_config.pb';
import { CreateResponse } from '@weave/schema-gen-ts/dist/schemas/phone/callqueue-config/v1/callqueue_config_service.pb';
import { Ringtone } from '@weave/schema-gen-ts/dist/shared/phonedata/v1/tenant.pb';
import { PhoneConfigOrg } from '@wetags/phone-config';
import CallQueueAPI from '@frontend/api-call-queue';
import { CallRouteV1 } from '@frontend/api-call-route';
import { DevicesQueries } from '@frontend/api-devices';
import { AnimatedBadge } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useQuery } from '@frontend/react-query-helpers';
import { sentry } from '@frontend/tracking';
import { validateName } from '@frontend/validation';
import { theme } from '@frontend/theme';
import {
  Button,
  DropdownField,
  InlineNotification,
  SkeletonLoaders,
  TextField,
  useAlert,
  useForm,
  useModalControl,
  useMultiStepModalControl,
} from '@frontend/design-system';
import { queryKeys } from '../../../../query-keys';
import { useAudioQuery } from '../../../../utils/use-audio-query';
import { CreateModal } from '../../../call-queue/new/create-button';
import { CallQueueSettingTray } from '../../call-queue-setting-tray';
import {
  RingtoneDropdown,
  type RingtoneOption,
  DEFAULT_RINGTONE_VALUE,
} from './atoms/ringtone-dropdown/ringtone-dropdown';
import { filterRingtonesForDevices, getConsolidatedDisplayName } from './atoms/ringtone-dropdown/ringtone-utils';
import { Step } from './atoms/step';
import { CallerLabelBaseText, getRemainingCharacters } from './characters-helpers';
import { DevicesPreview, DeviceType, PreviewDeviceObject } from './devices-preview';
import { StepContext } from './provider';
import { AddStepPanelProps } from './types';

export const CallQueueStep = ({
  tenantId,
  initialState,
  callObjectsData,
  onProceedClick,
}: AddStepPanelProps<CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE>) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const stepContext = useContext(StepContext);
  const [ringtonesEnabled] = PhoneConfigOrg.useWetag('callqueue-device-ringtones');

  const { triggerProps, ...createCallQueueModalProps } = useMultiStepModalControl(
    { onComplete: () => {}, onCancel: () => {}, showCancelAction: true },
    { onClose: () => {} }
  );

  const callQueueTrayModalControl = useModalControl();

  const handleOpenCreateCallQueue = () => {
    createCallQueueModalProps.goToStep({ orIndex: 0 });
    triggerProps.onClick();
  };

  const { data: ringtones = [] } = DevicesQueries.useRingtones(
    {},
    { enabled: ringtonesEnabled, select: (data) => data.ringtones, retry: false }
  );

  const { getFieldProps, isComplete, values } = useForm({
    fields: {
      callQueueId: {
        required: true,
        type: 'dropdown',
        value: initialState?.callObject?.primitiveId,
      },
      callerLabel: {
        required: false,
        type: 'text',
        value: initialState?.callObject?.callQueueExpansion?.callerLabel,
        validator: validateName,
        validateOnChange: true,
      },
      ringtone: {
        type: 'dropdown',
        required: false,

        // Map RINGTONE_UNSPECIFIED (the backends's value that represents the default ringtone) to DEFAULT_RINGTONE_VALUE
        // so that the RingtoneDropdown's synthetic "Default Ringtone" consolidated option is
        // pre-selected when editing an existing node that was saved with the default.
        value:
          (initialState?.callObject?.callQueueExpansion?.ringtone?.ringtoneType ?? Ringtone.RINGTONE_UNSPECIFIED) ===
          Ringtone.RINGTONE_UNSPECIFIED
            ? DEFAULT_RINGTONE_VALUE
            : initialState?.callObject?.callQueueExpansion?.ringtone?.ringtoneType,
      },
    },
  });

  const selectedCallQueueId = values.callQueueId ?? '';

  const { data: audioData, isLoading: audioDataIsLoading } = useAudioQuery({
    tenantId,
    allowedOptions: { custom: true },
  });

  const { callQueues, callQueuesIsLoading, devices: allDevices = [] } = callObjectsData;

  const {
    data: callQueue,
    isLoading: callQueueIsLoading,
    refetch,
  } = useQuery({
    queryKey: [settingsTenantLocation?.phoneTenantId, ...queryKeys.settings.readCallQueue(selectedCallQueueId)],
    queryFn: () => CallQueueAPI.Read({ callQueueId: selectedCallQueueId }),
    staleTime: 30 * 1000,
    retry: false,
    enabled: !!selectedCallQueueId,
  });

  const callQueueGreeting = audioData?.customMedia.find((audio) => audio.id === callQueue?.greetingMediaId);

  // Filter to devices that have a recognized make. Devices with an empty or
  // "unspecified" make carry no useful info about which ringtones to offer.
  const devicesWithKnownMake = useMemo(
    () =>
      allDevices.filter((device) => {
        const make = (device.make ?? '').toLowerCase();
        return make !== '' && make !== 'unspecified';
      }),
    [allDevices]
  );

  // When no devices have a known make (no devices exist, or every device has an
  // empty/unspecified make), fall back to generic Yealink ringtones so the
  // dropdown always has options to display.
  const hasYealinkDevice = useMemo(
    () =>
      devicesWithKnownMake.length === 0 ||
      devicesWithKnownMake.some((device) => device.make.toLowerCase() === 'yealink'),
    [devicesWithKnownMake]
  );
  const hasPolycomDevice = useMemo(
    () => devicesWithKnownMake.some((device) => device.make.toLowerCase() === 'polycom'),
    [devicesWithKnownMake]
  );

  const filteredRingtones: RingtoneOption[] = useMemo(
    () => filterRingtonesForDevices(ringtones, { hasYealinkDevice, hasPolycomDevice }),
    [ringtones, hasYealinkDevice, hasPolycomDevice]
  );

  const processedDevices: PreviewDeviceObject[] = useMemo(() => {
    const devices = callQueue?.devices?.reduce<PreviewDeviceObject[]>((acc, device) => {
      if (device.type === DeviceType_Enum.DESK_PHONE) {
        return [
          ...acc,
          {
            name: device.name,
            type: DeviceType.Desk,
            locationIds: device.locationIds,
          },
        ];
      } else if (device.type === DeviceType_Enum.SOFTPHONE) {
        return [
          ...acc,
          {
            name: device.name,
            type: DeviceType.Softphone,
            locationIds: device.locationIds,
          },
        ];
      } else {
        return [
          ...acc,
          {
            name: device.name,
            type: device.type ?? DeviceType.Unknown,
            locationIds: device.locationIds,
          },
        ];
      }
    }, []);

    return (devices ?? []).sort((a, b) => a.name.localeCompare(b.name));
  }, [callQueue]);

  const handleProceedClick = useCallback(async () => {
    // We need to fetch the callQueue data by the selected callQueueId so that we can get the
    // associated greeting media data (if any) to pass to the call object.
    const callQueueResp = await refetch();
    const callQueue = callQueueResp.data;

    if (!callQueue) {
      alerts.error('Failed to fetch call queue data.');
      sentry.error({
        error: 'Call queue not found when proceeding with call queue step',
        topic: 'phone-config',
        team: 'fe-phone-config',
        severityLevel: 'error',
        addContext: {
          name: 'Step data',
          context: {
            callQueueId: values.callQueueId,
            callQueueRespError: callQueueResp.error,
          },
        },
      });
      return;
    }

    // Get the greeting media data for the selected call queue (if it exists).
    // NOTE: We only need this data to show to the user in the node expansion details, if
    // there are any issues with the audio data, we can still proceed with the call object creation
    // without it and do not need to show an error to the user.
    const greetingMediaId = callQueue.greetingMediaId;
    const greetingMedia = audioData?.customMedia.find((audio) => audio.id === greetingMediaId);

    const deviceNames = processedDevices.map((device) => device.name);

    const selectedRingtone = ringtones.find((r) => r.ringtoneType === values.ringtone);
    // DEFAULT_RINGTONE_VALUE is the custom value used by the synthetic "Default Ringtone" option.
    // No real ringtone has this ringtoneType, so ringtones.find() above will not match it.
    // We detect it explicitly here and map it back to RINGTONE_UNSPECIFIED for the API payload.
    const isDefaultSelected = values.ringtone === DEFAULT_RINGTONE_VALUE;

    onProceedClick({
      type: CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE,
      callObject: {
        primitiveId: callQueue.callQueueId,
        primitiveName: callQueue.name,
        instructionId: initialState?.callObject?.instructionId ?? '',
        instructionSetId: initialState?.callObject?.instructionSetId ?? '',
        callQueueExpansion: {
          greetingMediaItemId: callQueue.greetingMediaId,
          downloadUrl: greetingMedia?.path,
          greetingMediaFileName: greetingMedia?.name,
          callerLabel: values.callerLabel,
          deviceCount: deviceNames.length,
          deviceNames,
          ringtone: isDefaultSelected
            ? {
                ringtoneType: Ringtone.RINGTONE_UNSPECIFIED,
                displayName: 'Default Ringtone',
              }
            : selectedRingtone
            ? {
                ringtoneType: selectedRingtone.ringtoneType,
                displayName: getConsolidatedDisplayName({
                  filteredRingtones,
                  selectedRingtone,
                  selectedRingtoneType: values.ringtone,
                }),
              }
            : undefined,
        },
      },
    });
  }, [
    refetch,
    alerts,
    audioData?.customMedia,
    processedDevices,
    onProceedClick,
    initialState?.callObject?.instructionId,
    initialState?.callObject?.instructionSetId,
    values.callerLabel,
    values.callQueueId,
    values.ringtone,
    ringtones,
    filteredRingtones,
  ]);

  const handleOnCreateCallQueueSuccess = (data: CreateResponse) => {
    const newCallQueueId = data.callQueueId;

    // @ts-expect-error This is complaining thinking that name and value do not exist on the event target,
    // but they do.
    getFieldProps('callQueueId').onChange({ target: { name: 'callQueueId', value: newCallQueueId } });
  };

  const shouldShowSkeletonLoaders = callQueuesIsLoading || audioDataIsLoading;

  return (
    <>
      <Step
        title={t('Call Queue')}
        primaryButtonDisabled={!isComplete || callQueueIsLoading}
        onPrimaryButtonClick={handleProceedClick}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(3)};
            max-width: 318px;
          `}
        >
          <SkeletonLoaders.Loader isLoading={shouldShowSkeletonLoaders} shape='rectangle' height={50}>
            <DropdownField
              label={t('Select Call Queue')}
              {...getFieldProps('callQueueId')}
              placeholder={t('Select Call Queue')}
            >
              <DropdownField.Option
                css={css`
                  padding: 0;
                `}
                key={'create-call-queue'}
                value={''}
                isSelectable={false}
              >
                <Button
                  iconName='plus'
                  variant='tertiary'
                  onClick={handleOpenCreateCallQueue}
                  css={css`
                    width: 100%;
                    justify-content: start;
                  `}
                >
                  {t('Create Call Queue')}
                </Button>
              </DropdownField.Option>
              {callQueues.map((queue) => (
                <DropdownField.Option key={queue.callQueueId} value={queue.callQueueId}>
                  {queue.name}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </SkeletonLoaders.Loader>

          <SkeletonLoaders.Loader isLoading={shouldShowSkeletonLoaders} shape='rectangle' height={50}>
            <TextField
              label={t('Enter Caller Label (Optional)')}
              {...getFieldProps('callerLabel')}
              helperText={getRemainingCharacters(values.callerLabel)}
            />
            <CallerLabelBaseText value={values.callerLabel} error={getFieldProps('callerLabel').error} />
          </SkeletonLoaders.Loader>

          {ringtonesEnabled && (
            <SkeletonLoaders.Loader isLoading={shouldShowSkeletonLoaders} shape='rectangle' height={50}>
              <div
                css={css`
                  position: relative; /* This is needed to position the "New" badge */
                  display: flex;
                  flex-direction: column;
                  gap: ${theme.spacing(1)};
                `}
              >
                <RingtoneDropdown
                  {...getFieldProps('ringtone')}
                  label={t('Select Desk Phone Ringtone (Optional)')}
                  helperText={t('Choose a distinct ringtone so your staff can identify the purpose of the call')}
                  ringtones={filteredRingtones}
                  consolidateRingtoneType
                />

                {/* Temporary badge for new feature */}
                <span
                  css={css`
                    display: inline-flex;
                    position: absolute;
                    top: 8px;
                    right: -52px;
                  `}
                >
                  <AnimatedBadge label={t('New')} />
                </span>
                <InlineNotification
                  status='info'
                  size='small'
                  message={t(
                    'The ringtone options listed are for desk phones only. Wireless handsets will ring differently. Softphones will not be affected.'
                  )}
                  customStyles={css`
                    p {
                      white-space: normal;
                      overflow: visible;
                      text-overflow: unset;
                    }
                  `}
                />
              </div>
            </SkeletonLoaders.Loader>
          )}
        </div>

        <DevicesPreview
          callObjectName={t('Call Queue')}
          isLoading={callQueueIsLoading}
          showInitialState={!selectedCallQueueId}
          devices={processedDevices}
          greetingData={{
            mediaItemId: callQueueGreeting?.id ?? '',
            mediaPath: callQueueGreeting?.path ?? '',
            greetingName: callQueueGreeting?.name ?? '',
          }}
          editButton={
            callQueue ? (
              <Button
                variant='secondary'
                onClick={() => callQueueTrayModalControl.openModal()}
                css={css`
                  align-self: flex-start;
                `}
                trackingId='phone::setting::call-route:edit-callqueue-btn'
              >
                <Icon name='edit-small' />
                {t('Edit {{callObjectName}}', {
                  callObjectName: t('Call Queue'),
                })}
              </Button>
            ) : null
          }
        />
      </Step>

      {settingsTenantLocation && (
        <CreateModal
          tenantLocation={settingsTenantLocation}
          {...createCallQueueModalProps}
          onSuccess={handleOnCreateCallQueueSuccess}
        />
      )}

      {/* Call Queue Settings Tray */}
      <CallQueueSettingTray
        modalControls={callQueueTrayModalControl}
        mountTarget='#call-route-page-body-container'
        selectedCallQueue={callQueue}
        stepContext={stepContext}
      />
    </>
  );
};
